import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface DashboardData {
  totalRelatives: number;
  activeRelatives: number;
  deceasedRelatives: number;
  recentSignups: number;
}

export interface Relative {
  id: number;
  firstName: string;
  middleName?: string;
  lastName: string;
  email?: string;
  mobileNumber: string;
  dateOfBirth: string;
  gender: string;
  isDeceased: boolean;
  referralCode: string;
  parentReferralCode?: string;
  spouseReferralCode?: string;
  relationshipType?: string;
  username: string;
  isActive: boolean;
  isApproved: boolean;
  approvedAt?: string;
  approvedBy?: string;
  createdAt: string;
  lastLoginAt?: string;
  qualification?: string;
  profession?: string;
  profileImageUrl?: string;
  location?: string;
}

export interface RelativesResponse {
  relatives: Relative[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface PendingRelativesResponse {
  relatives: Relative[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  pendingCount: number;
}

export interface ApproveRelativeRequest {
  relativeId: number;
  approvedBy: string;
  approvalNotes?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AdminService {

  constructor(private http: HttpClient) { }

  getDashboardData(): Observable<DashboardData> {
    return this.http.get<DashboardData>(`${environment.apiUrl}/api/admin/dashboard`);
  }

  getRelatives(page: number = 1, pageSize: number = 10, search?: string): Observable<RelativesResponse> {
    let url = `${environment.apiUrl}/api/relative?page=${page}&pageSize=${pageSize}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    return this.http.get<RelativesResponse>(url);
  }

  // Manage Relatives CRUD
  addRelative(relative: Partial<Relative> & { password?: string }): Observable<Relative> {
    console.log('🔍 AdminService - addRelative called with:', relative);
    console.log('🔍 AdminService - API URL:', `${environment.apiUrl}/api/relative`);
    
    // Map camelCase to PascalCase for API compatibility
    const safeEmail = (relative.email && relative.email.toString().trim() !== '') ? relative.email : null;
    const safeMobile = (relative.mobileNumber && relative.mobileNumber.toString().trim() !== '') ? relative.mobileNumber : null;
    const safeLocation = ((relative as any).location && (relative as any).location.toString().trim() !== '') ? (relative as any).location : null;
    const mappedData = {
      FirstName: relative.firstName,
      MiddleName: relative.middleName,
      LastName: relative.lastName,
      Email: safeEmail,
      MobileNumber: safeMobile,
      DateOfBirth: relative.dateOfBirth,
      Gender: relative.gender,
      Qualification: relative.qualification,
      Profession: relative.profession,
      Location: safeLocation,
      ParentReferralCode: relative.parentReferralCode,
      RelationshipType: relative.relationshipType,
      SpouseReferralCode: relative.spouseReferralCode,
      IsDeceased: relative.isDeceased,
      Username: relative.username,
      Password: relative.password
    };
    
    console.log('🔍 AdminService - Mapped request data:', mappedData);
    return this.http.post<Relative>(`${environment.apiUrl}/api/relative`, mappedData);
  }

  updateRelative(id: number, relative: Partial<Relative> & { password?: string }): Observable<Relative> {
    // Map camelCase to PascalCase for API compatibility
    const safeEmail = (relative.email && relative.email.toString().trim() !== '') ? relative.email : null;
    const safeMobile = (relative.mobileNumber && relative.mobileNumber.toString().trim() !== '') ? relative.mobileNumber : null;
    const safeLocation = ((relative as any).location && (relative as any).location.toString().trim() !== '') ? (relative as any).location : null;
    const mappedData = {
      FirstName: relative.firstName,
      MiddleName: relative.middleName,
      LastName: relative.lastName,
      Email: safeEmail,
      MobileNumber: safeMobile,
      DateOfBirth: relative.dateOfBirth,
      Gender: relative.gender,
      Qualification: relative.qualification,
      Profession: relative.profession,
      Location: safeLocation,
      ReferralCode: relative.referralCode, // Include referral code for admin updates
      ParentReferralCode: relative.parentReferralCode,
      RelationshipType: relative.relationshipType,
      SpouseReferralCode: relative.spouseReferralCode,
      IsDeceased: relative.isDeceased,
      Username: relative.username,
      Password: relative.password
    };
    
    console.log('🔍 AdminService - updateRelative called with:', mappedData);
    return this.http.put<Relative>(`${environment.apiUrl}/api/relative/${id}`, mappedData);
  }

  deleteRelative(id: number): Observable<any> {
    return this.http.delete(`${environment.apiUrl}/api/relative/${id}`);
  }

  getRelative(id: number): Observable<Relative> {
    return this.http.get<Relative>(`${environment.apiUrl}/api/relative/${id}`);
  }

  toggleRelativeStatus(id: number): Observable<any> {
    return this.http.patch(`${environment.apiUrl}/api/relative/${id}/toggle-status`, {});
  }

  // Approval Operations
  getPendingRelatives(page: number = 1, pageSize: number = 10, search?: string): Observable<PendingRelativesResponse> {
    let url = `${environment.apiUrl}/api/relative/pending?page=${page}&pageSize=${pageSize}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    return this.http.get<PendingRelativesResponse>(url);
  }

  approveRelative(request: ApproveRelativeRequest): Observable<any> {
    return this.http.post(`${environment.apiUrl}/api/relative/approve`, request);
  }

  approveRelativesBatch(requests: ApproveRelativeRequest[]): Observable<any> {
    return this.http.post(`${environment.apiUrl}/api/relative/approve-batch`, requests);
  }

  // Hierarchy Operations
  getRelativeHierarchy(relativeId: number): Observable<HierarchyData> {
    return this.http.get<HierarchyData>(`${environment.apiUrl}/api/relative/${relativeId}/hierarchy`);
  }

  // Get relative by ID
  getRelativeById(relativeId: number): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/api/relative/${relativeId}`);
  }

  // Admin Operations - Get actual password for admin users
  getRelativePassword(relativeId: number): Observable<{password: string}> {
    return this.http.get<{password: string}>(`${environment.apiUrl}/api/admin/relative/${relativeId}/password`);
  }

  // Self Profile Operations
  getMyProfile(): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/api/relative/me`);
  }

  updateMyProfile(relative: Partial<Relative> & { password?: string }): Observable<any> {
    const safeEmail = (relative.email && relative.email.toString().trim() !== '') ? relative.email : null;
    const safeMobile = (relative.mobileNumber && relative.mobileNumber.toString().trim() !== '') ? relative.mobileNumber : null;
    const safeLocation = ((relative as any).location && (relative as any).location.toString().trim() !== '') ? (relative as any).location : null;
    const mappedData = {
      FirstName: relative.firstName,
      MiddleName: relative.middleName,
      LastName: relative.lastName,
      Email: safeEmail,
      MobileNumber: safeMobile,
      DateOfBirth: relative.dateOfBirth,
      Gender: relative.gender,
      Qualification: relative.qualification,
      Profession: relative.profession,
      Location: safeLocation,
      IsDeceased: relative.isDeceased,
      Username: relative.username,
      Password: relative.password
    };

    return this.http.put<any>(`${environment.apiUrl}/api/relative/me`, mappedData);
  }

  uploadMyProfileImage(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(`${environment.apiUrl}/api/relative/me/profile-image`, formData);
  }

  uploadRelativeProfileImage(relativeId: number, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(`${environment.apiUrl}/api/relative/${relativeId}/profile-image`, formData);
  }

  // Settings Operations
  getSettings(): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/api/admin/settings`);
  }

  updateSettings(retentionPeriodDays: number): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}/api/admin/settings`, {
      retentionPeriodDays
    });
  }

  archivePosts(): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/api/admin/archive-posts`, {});
  }
}

export interface HierarchyData {
  parents: Relative[];
  siblings: Relative[];
  spouse: Relative | null;
  children: Relative[];
  current: Relative;
}

export interface SettingsData {
  retentionPeriodDays: number;
  lastUpdated?: string;
}

export interface ArchiveResult {
  archivedCount: number;
  retentionPeriodDays: number;
  cutoffDate: string;
  archivedAt: string;
}
